import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TableCell,
  TableBody,
} from "@mui/material";
import CommonAccordion from "../../custom-components/CommonAccordion";
import MultiAccordian from "../testcase-generation/MultiAccordian";
import {
  investigate,
  userstoryEvaluation,
  validateUserInput,
} from "../../../services/webServices";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  MultipleUserStoriesContext,
  UserStoriesLoaderContext,
} from "../../main-pages/MultipleUserStoryPage";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import CommonDownload from "../../custom-components/CommonDownload";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SelectionErrorPopUp from "../../popup-components/SelectionerrorPop";
import { flattenArray } from "../../../utils/dataFlatteners";
import EditableTable from "./EditableTable";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

const MultipleUserStoriesComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { userStoryLoaderState, setUserStoryLoaderState } = useContext(
    UserStoriesLoaderContext
  );
  const {
    selectedUserStories,
    setSelectedUserStories,
    multipleEvaluationSummary,
    setMultipleEvaluationSummary,
  } = useContext(MultipleUserStoriesContext);

  const [multipleUserStories, setMultipleUserStories] = useState(
    location.state?.userStories
  );
  const defaultUserStoriesExpanded =
    multipleUserStories.length > 0 && multipleEvaluationSummary.length === 0;
  const { setLoadingState } = useContext(LoadingModalContext);

  const [investParameter, setInvestParameters] = useState([
    "Independent",
    "Negotiable",
    "Valuable",
    "Estimable",
    "Small",
    "Testable",
  ]);
  const [multiEvaluationSummary, setMultiEvaluationSummary] = useState([]);
  const [isEditing, setIsEditing] = useState(null);
  const [openDownload, setOpenDownload] = useState(false);
  const [editingValue, setEditingValue] = useState({});
  const [noneSelectionPopOpen, setNoneSelectionPopOpen] = useState(false);

  useEffect(() => {
    setMultipleEvaluationSummary(multiEvaluationSummary);
  }, [multiEvaluationSummary]);

  useEffect(() => {
    navigate("/dashboard/multi-userstory/output", {
      state: {
        ...location.state,
        userStories: multipleUserStories,
        selectedUserStories: selectedUserStories,
      },
      replace: true,
    });
  }, [multipleUserStories]);

  const summaries = useMemo(
    () =>
      multipleUserStories.map((eachUserStory) => {
        return eachUserStory["Title"];
      }),
    [multipleUserStories]
  );

  const hasSelectedUserStories = useMemo(() => {
    return selectedUserStories.some((story) => !!story);
  }, [selectedUserStories]);

  const processText = (text, isAdditionalInfo = false) => {
    // Convert array input to string with newlines, or handle non-string input safely
    let processedText = "";

    const isArray = Array.isArray(text);
    if (isArray) {
      processedText = text.join("\n");
    } else {
      processedText =
        typeof text === "string" ? text : text != null ? String(text) : "";
    }

    if (isAdditionalInfo && !isArray) {
      // Only apply bullet formatting for lines starting with '*'
      processedText = processedText
        .split("\n")
        .map((line) => {
          if (line.trim().startsWith("*")) {
            // Apply bold formatting inside bullet
            let boldedLine = line.replace(/\*(.*?)\*/g, "<strong>$1</strong>");
            boldedLine = boldedLine.replace(/:\*/g, ":");
            return `<li style="margin-top: 14px; line-height: 0.1;">${boldedLine}</li>`;
          }
          return line;
        })
        .join("<br />");
    } else {
      // Apply bolding and line breaks normally
      processedText = processedText
        .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br />");
    }

    return processedText;
  };

  const userStoriesComponents = multipleUserStories.map(
    (eachUserStory, userStoryIndex) => (
      <Box key={`eachUserStory_${userStoryIndex}`}>
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: "14px",
            marginLeft: 1,
            fontWeight: "bold",
            color: "#000",
          }}
        >
          Title
        </Typography>
        {isEditing === userStoryIndex ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              name="Title"
              value={editingValue["Title"]}
              onChange={(e) => handleEdittedValue(e, userStoryIndex)}
              sx={{ marginTop: 1, marginRight: 1 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <Typography
              variant="body1"
              sx={{ marginBottom: 2, fontSize: "14px" }}
            >
              {eachUserStory["Title"]}
            </Typography>
          </Box>
        )}

        {/* Description */}
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: "14px",
            marginLeft: 1,
            fontWeight: "bold",
            color: "#000",
          }}
        >
          Description
        </Typography>
        {isEditing === userStoryIndex ? (
          //   <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          //     <TextField
          //       fullWidth
          //       multiline
          //       rows={4}
          //       variant="outlined"
          //       name="Description"
          //       value={editingValue["Description"]}
          //       onChange={(e) => handleEdittedValue(e, userStoryIndex)}
          //       sx={{ marginTop: 1, marginRight: 1 }}
          //       inputProps={{ style: { fontSize: "14px" } }}
          //     />
          //   </Box>
          // ) : (
          //   <Box sx={{ display: "flex", flexDirection: "column", gap: 1, "&:hover": { backgroundColor: "white" } }}>
          //     <Typography variant="body1" sx={{ fontSize: "14px", marginBottom: 1, whiteSpace: "pre-line" }} dangerouslySetInnerHTML={{ __html: processText(eachUserStory["Description"]) }} />
          //   </Box>
          // )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {(() => {
              const description = editingValue["Description"] || "";

              const lines = description.split("\n");
              const tableLines = lines.filter((line) =>
                /^\|.*\|$/.test(line.trim())
              );
              const tableRows = tableLines.map((line) =>
                line
                  .trim()
                  .split("|")
                  .filter(Boolean)
                  .map((cell) => cell.trim())
              );

              const hasTable = tableRows.length > 1;

              const handleTableUpdate = (updatedRows) => {
                // Convert the updated rows into a Markdown table format
                const tableMarkdown = updatedRows
                  .map((row) => `| ${row.join(" | ")} |`)
                  .join("\n");

                // Update the Description field with the new Markdown table
                editingValue["Description"] = tableMarkdown;
                setEditingValue({ ...editingValue }); // Force re-render to update UI
              };

              return (
                <>
                  {/* Show the editable table instead of the TextField if there's a table */}
                  {hasTable ? (
                    <EditableTable
                      initialRows={tableRows}
                      onRowsChange={handleTableUpdate}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      name="Description"
                      value={editingValue["Description"]}
                      onChange={(e) => handleEdittedValue(e, userStoryIndex)}
                      sx={{ marginTop: 1, marginRight: 1 }}
                      inputProps={{ style: { fontSize: "14px" } }}
                    />
                  )}
                </>
              );
            })()}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              "&:hover": { backgroundColor: "white" },
            }}
          >
            {(() => {
              //dangerouslySetInnerHTML={{ __html: processText(eachUserStory["Description"]) }}
              const description = eachUserStory["Description"] || "";

              // Split lines
              const lines = description.split("\n");

              // Extract table lines
              const tableLines = lines.filter((line) =>
                /^\|.*\|$/.test(line.trim())
              );

              // Parse table if found
              const tableRows = tableLines.map((line) =>
                line
                  .trim()
                  .split("|")
                  .filter(Boolean)
                  .map((cell) => cell.trim())
              );

              const hasTable = tableRows.length > 0;

              // Non-table text: everything except the table lines
              const nonTableText = lines
                .filter((line) => !/^\|.*\|$/.test(line.trim()))
                .join("\n")
                .trim();

              return (
                <>
                  {nonTableText && (
                    // <Typography
                    //   variant="body1"
                    //   sx={{
                    //     fontSize: "14px",
                    //     marginBottom: 1,
                    //     whiteSpace: "pre-line",
                    //   }}
                    // >
                    //   {nonTableText}
                    // </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: "14px", marginBottom: 2 }}
                      dangerouslySetInnerHTML={{
                        __html: processText(nonTableText),
                      }}
                    />
                  )}

                  {hasTable && (
                    <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                            {tableRows[0].map((header, i) => (
                              <TableCell
                                key={i}
                                sx={{ color: "#1976d2", fontWeight: "bold" }}
                              >
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tableRows.slice(1).map((row, i) => (
                            <TableRow key={i}>
                              {row.map((cell, j) => (
                                <TableCell key={j}>{cell}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              );
            })()}
          </Box>
        )}

        {/* Acceptance Criteria */}
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: "14px",
            marginLeft: 1,
            fontWeight: "bold",
            color: "#000",
          }}
        >
          Acceptance Criteria
        </Typography>
        {isEditing === userStoryIndex ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              name="Acceptance Criteria"
              value={editingValue["Acceptance Criteria"]}
              onChange={(e) => handleEdittedValue(e, userStoryIndex)}
              sx={{ marginTop: 1, marginRight: 1 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "14px", marginBottom: 2 }}
              dangerouslySetInnerHTML={{
                __html: processText(eachUserStory["Acceptance Criteria"]),
              }}
            />
          </Box>
        )}

        {/* Additional Information */}
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: "14px",
            marginLeft: 1,
            fontWeight: "bold",
            color: "#000",
          }}
        >
          Additional Information
        </Typography>
        {isEditing === userStoryIndex ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              name="Additional Information"
              value={editingValue["Additional Information"]}
              onChange={(e) => handleEdittedValue(e, userStoryIndex)}
              sx={{ marginTop: 1, marginRight: 1 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "14px" }}
              dangerouslySetInnerHTML={{
                __html: processText(
                  eachUserStory["Additional Information"],
                  true
                ),
              }}
            />
          </Box>
        )}
      </Box>
    )
  );

  const handleDownload = () => {
    setOpenDownload(true);
  };

  const handleEdittedValue = (event) => {
    const updatedValue = {
      ...editingValue,
      [event.target.name]: event.target.value,
    };
    setEditingValue(updatedValue);
  };

  const handleMultiEvaluationSummary = async () => {
    let progressHandler;
    progressHandler = handleProgressBar({
      setLoadingState,
      totalDurationMs: 18000,
    });
    const initialMsg =
      selectedUserStories.length === 1
        ? "Evaluating User Story"
        : "Evaluating User Stories";
    try {
      progressHandler.start(initialMsg);
      setUserStoryLoaderState("Evaluation Summary");
      const updatedEvaluationSummaries = new Array(
        selectedUserStories.length
      ).fill(null);
      let isRAGavailableFlag = false;

      // Use Promise.all for concurrent processing
      await Promise.all(
        selectedUserStories.map(async (eachUserStory, index) => {
          if (eachUserStory && Object.keys(eachUserStory).length > 0) {
            const requestData = {
              data: `Title: ${eachUserStory["Title"]}
                     Description: ${eachUserStory["Description"]}
                     Acceptance Criteria: ${eachUserStory["Acceptance Criteria"]}
                     Additional Information: ${eachUserStory["Additional Information"]}`,
              parameters: investParameter,
            };

            // Call userInvest for core evaluation check
            const userInvestResponse = await validateUserInput(requestData);
            const userInvestResult =
              userInvestResponse.data?.Result?.toLowerCase();

            if (userInvestResult !== "clean") {
              const errorMsg =
                userInvestResponse?.data?.message ||
                userInvestResponse?.data?.Reason ||
                "Inappropriate user story detected. Please revise and try again.";

              progressHandler.stop();
              return;
            }
            // const evaluationSummary = await investigate(requestData);
            const evaluationSummary = await userstoryEvaluation(requestData);
            updatedEvaluationSummaries[index] =
              evaluationSummary.data.evaluation;
            const isRAGavaiable = evaluationSummary.data.isRAGavaiable;
            console.log("isRAGavaiable================>", isRAGavaiable);
            if (evaluationSummary.data.isRAGavaiable) {
              isRAGavailableFlag = true;
            }
          }
        })
      );

      // Batch state updates after processing
      setMultiEvaluationSummary(updatedEvaluationSummaries);

      await progressHandler.finishAndClose();
      navigate("/dashboard/multi-userstory/output", {
        state: {
          ...location.state,
          userStories: multipleUserStories,
          selectedUserStories,
          multiEvaluationSummary: updatedEvaluationSummaries,
          multipleRefinedUserStory: [],
          selectedRefinedUserStories: [],
          multipleUseCases: [],
          selectedMultipleUseCases: [],
          multipleTestCases: [],
          isRAGavaiable: isRAGavailableFlag,
        },
        replace: true,
      });
      setUserStoryLoaderState("");
    } catch (error) {
      console.error(error);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const createWordData = (multipleUserStoriesData) => {
    const wordData = [];
    multipleUserStoriesData.map((eachUserStory) => {
      if (!!eachUserStory) {
        wordData.push({
          heading: "User Story",
          type: "subParagraphs",
          data: {
            Title: eachUserStory["Title"],
            Description: eachUserStory["Description"],
            "Acceptance Criteria": eachUserStory["Acceptance Criteria"],
            "Additional Information": eachUserStory["Additional Information"],
          },
        });
        wordData.push({ type: "pageBreak" });
        return;
      }
    });
    return wordData;
  };

  const buttons = [
    {
      label: "Download",
      onClick: handleDownload,
      variant: "text",
      startIcon: <FileDownloadOutlinedIcon />,
      sx: { textTransform: "none" },
      disabled: !hasSelectedUserStories,
    },
    {
      label: "Evaluate",
      variant: "contained",
      onClick: handleMultiEvaluationSummary,
      sx: { float: "right" },
      disabled: !!userStoryLoaderState || !hasSelectedUserStories, //changed
    },
  ];

  return (
    <Box sx={{ mx: "auto", height: "100%" }}>
      <CommonAccordion
        summary={`User Stories (${multipleUserStories.length})`}
        details={
          <Box>
            <Box sx={{ overflow: "hidden", width: "100%" }}>
              <MultiAccordian
                summaries={summaries}
                multiAccoridanDetails={userStoriesComponents}
                multiAccoridanData={multipleUserStories}
                setMultiAccordianData={setMultipleUserStories}
                selectedAccordians={selectedUserStories}
                setSelectedAccordians={setSelectedUserStories}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                editingValue={editingValue}
                setEditingValue={setEditingValue}
                loaderCheck={!!userStoryLoaderState}
              />
            </Box>
          </Box>
        }
        buttons={buttons}
        type={"output"}
        defaultExpanded={defaultUserStoriesExpanded}
        fullScreenMode={true}
      />
      <SelectionErrorPopUp
        open={noneSelectionPopOpen}
        onClose={() => setNoneSelectionPopOpen(false)}
      />
      <CommonDownload
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        serviceResponse={[...createWordData(selectedUserStories)]}
        serviceName="User Story"
        wordData={createWordData(selectedUserStories)}
        documents={["docx", "pdf"]}
      />
    </Box>
  );
};

export default MultipleUserStoriesComponent;
